import { Combobox } from "@headlessui/react";
import classNames from "classnames";
import { useTranslations } from "next-intl";
import { CSSProperties, Fragment, useEffect, useId, useMemo, useRef, useState } from "react";
import { Control, Controller } from "react-hook-form";

import CommonUtils from "~/utils/common-utils";
import Constants from "~/utils/constants";

import Icon from "../common/icon";
import styles from "./controlled-combobox.module.scss";

type ControlledComboboxOption = { value: any; label: string };

type ControlledComboboxProps = {
  name: string;
  options: ControlledComboboxOption[];
  control: Control<any>;
  disabled?: boolean;
  error?: string;
  hidden?: boolean;
  hideInputBorder?: boolean;
  informativeMessage?: string;
  isOptional?: boolean;
  label?: string;
  maxOptions?: number;
  minTypedChars?: number;
  successMessage?: string;
  tabIndex?: number;
  customClass?: string;
};

export default function ControlledCombobox(props: ControlledComboboxProps) {
  const opts = Array.isArray(props.options) ? props.options : [];
  const innerMaxOptions = typeof props.maxOptions === "number" ? Math.min(props.maxOptions, 150) : 150;
  const innerMinTypedChars = typeof props.minTypedChars === "number" ? Math.max(props.minTypedChars, 0) : 0;

  const t = useTranslations();
  const id = useId();

  const [query, setQuery] = useState("");
  const canSearch = query.length >= innerMinTypedChars;

  const comboboxPanelClassnames = useMemo(() => [styles.comboboxOptionsPanel], []);
  const [isInputFocused, setInputFocused] = useState(false);

  //the width of the label that will be used to create a transparent label is kept track of
  const refLabel = useRef<HTMLLabelElement>(null);
  const [widthLabel, setWidthLabel] = useState<number | undefined>();
  const styleLabelLine = {
    "--label-line-width": `calc(100% - ${widthLabel}px - var(--label-external-padding-horizontal))`,
  } as CSSProperties;

  const filteredOpts = (
    canSearch
      ? opts.filter((opt: ControlledComboboxOption) => {
          return opt.label.toLowerCase().includes(query.toLowerCase());
        })
      : []
  ).slice(0, innerMaxOptions);

  const getOptionByValue = (value: string): ControlledComboboxOption | void => {
    return opts.find((o: ControlledComboboxOption) => o.value === value);
  };

  const getMaskedValue = (value: string): string => {
    return getOptionByValue(value)?.label || "";
  };

  useEffect(() => {
    CommonUtils.isWindows() && comboboxPanelClassnames.push(Constants.SCROLLBAR_CLASSNAME);
    setWidthLabel(refLabel.current?.offsetWidth);
  }, [props.isOptional, comboboxPanelClassnames]);

  return (
    <div
      className={classNames(
        styles.container,
        !props.hideInputBorder && props.error ? styles.containerWithErrors : null,
        !props.hideInputBorder && isInputFocused ? styles.containerInFocus : null,
        props.hidden ? styles.hidden : null,
        props.customClass ?? null
      )}
    >
      {!props.hideInputBorder ? (
        <div className={styles.wrapperLabelField}>
          <label
            htmlFor={id}
            ref={refLabel}
            style={styleLabelLine}
            className={styles.labelField}
            onMouseDown={(e) => e.preventDefault()}
          >
            {props.label}
            {props.isOptional ? (
              ""
            ) : (
              <span className={styles.mandatory} aria-hidden>
                {t("generic.form.mandatory_short")}
              </span>
            )}
          </label>
        </div>
      ) : (
        // Add the label element in case of phone input form
        <label className="sr-only" htmlFor={id}>
          {t("a11y.countryCode")}
        </label>
      )}
      <div
        className={classNames(
          styles.wrapperInputField,
          props.disabled ? styles.wrapperInputFieldUneditable : null,
          props.hideInputBorder ? styles.noBorder : null
        )}
        onFocus={() => setInputFocused(true)}
        onBlur={(e) => {
          // if relatedTarget is not defined or not inside wrapperInputField
          if (!e.relatedTarget || !e.currentTarget.contains(e.relatedTarget)) {
            setInputFocused(false);
          }
        }}
      >
        <Controller
          defaultValue={""}
          name={props.name}
          control={props.control}
          render={({ field }) => {
            return (
              <Combobox
                value={field.value}
                onChange={field.onChange} // when clicking inside the combobox
                disabled={props.disabled}
                nullable
              >
                <Combobox.Input
                  id={id}
                  ref={field.ref}
                  displayValue={(currentValue: string) => getMaskedValue(currentValue)}
                  className={styles.inputField}
                  onChange={(event) => {
                    setQuery(event.target.value);
                    // force on field.onChange here for capturing the autofill event that cause state inconcistency
                    field.onChange(event);
                  }}
                  tabIndex={props.tabIndex}
                  autoComplete="_off"
                />
                <Combobox.Button className={styles.toggle} aria-label={t("a11y.countryCodeList")}>
                  <Icon name="drag" aria-hidden="true" width={16} height={16} />
                </Combobox.Button>
                {isInputFocused && (
                  <Combobox.Options className={classNames(...comboboxPanelClassnames)}>
                    {filteredOpts.map((opt: ControlledComboboxOption) => (
                      <Combobox.Option key={opt.value} value={opt.value} as={Fragment}>
                        {({ active }) => <li className={classNames(active && styles.activeOption)}>{opt.label}</li>}
                      </Combobox.Option>
                    ))}
                    {canSearch && !filteredOpts.length && (
                      <Combobox.Option disabled={true} value={undefined} className={styles.noResult}>
                        {t("generic.combobox_no_results")}
                      </Combobox.Option>
                    )}
                  </Combobox.Options>
                )}
              </Combobox>
            );
          }}
        />
      </div>
      {props.error ? (
        <p id={`${id}-error`} className={styles.errorMessage}>
          {props.error}
        </p>
      ) : props.successMessage ? (
        <p className={styles.successMessage}>{props.successMessage}</p>
      ) : props.informativeMessage ? (
        <p className={styles.informativeMessage}>{props.informativeMessage}</p>
      ) : (
        <></>
      )}
    </div>
  );
}
